import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  hub: () => client.get("/hub").then(r => r.data),
  runDryRun: ({ cleanup = true } = {}) =>
    client.post("/diagnostics/dry-run", { cleanup },
                { timeout: 180000 }).then(r => r.data),
  // ── RIE-1 · Reconciling Items (Feb 2026) ─────────────────────────────
  listReconcilingItems: periodId =>
    client.get(`/periods/${periodId}/reconciling-items`).then(r => r.data),
  createReconcilingItem: (periodId, body) =>
    client.post(`/periods/${periodId}/reconciling-items`, body).then(r => r.data),
  updateReconcilingItem: (riId, body) =>
    client.patch(`/reconciling-items/${riId}`, body).then(r => r.data),
  deleteReconcilingItem: riId =>
    client.delete(`/reconciling-items/${riId}`).then(r => r.data),
  listRiCategories: () =>
    client.get("/reconciling-items/categories").then(r => r.data),
  getActiveCloseChecklist: () =>
    client.get("/periods/active-checklist").then(r => r.status === 204 ? null : r.data),
  accounts: () => client.get("/accounts").then(r => r.data),
  periods: (params = {}) => client.get("/periods", { params }).then(r => r.data),
  openNextPeriod: (accountId) =>
    client.post("/periods/open-next", { account_id: accountId }).then(r => r.data),
  workspace: (periodId) => client.get(`/periods/${periodId}/workspace`).then(r => r.data),

  editBalances: (periodId, payload) =>
    client.patch(`/periods/${periodId}/balances`, payload).then(r => r.data),

  importFile: (periodId, importType, file, options = {}) => {
    const fd = new FormData();
    fd.append("import_type", importType);
    fd.append("file", file);
    if (options.replacement_decision) {
      fd.append("replacement_decision", options.replacement_decision);
    }
    if (options.replacement_reason) {
      fd.append("replacement_reason", options.replacement_reason);
    }
    if (options.superseded_batch_id) {
      fd.append("superseded_batch_id", options.superseded_batch_id);
    }
    if (options.supersede_mode) {
      fd.append("supersede_mode", options.supersede_mode);
    }
    return client.post(`/periods/${periodId}/imports`, fd).then(r => r.data);
  },

  getSupersedeImpact: (batchId) =>
    client.get(`/imports/batches/${batchId}/supersede-impact`).then(r => r.data),

  reclassify: (tid, classification, reason) =>
    client.patch(`/transactions/${tid}/reclassify`, { classification, reason }).then(r => r.data),
  bulkReclassify: (transaction_ids, classification, reason) =>
    client.post(`/transactions/bulk-reclassify`, { transaction_ids, classification, reason }).then(r => r.data),
  exclude: (tid, exclusion_type, reason) =>
    client.patch(`/transactions/${tid}/exclude`, { exclusion_type, reason }).then(r => r.data),
  restore: (tid, reason) =>
    client.post(`/transactions/${tid}/restore`, { reason }).then(r => r.data),

  createMatchGroup: (data) => client.post("/match-groups", data).then(r => r.data),
  deleteMatchGroup: (mgId, reason) => client.delete(`/match-groups/${mgId}`, { params: { reason } }).then(r => r.data),

  detectDuplicates: (periodId) => client.post(`/periods/${periodId}/detect-duplicates`).then(r => r.data),
  disposeCluster: (cid, data) => client.post(`/duplicate-clusters/${cid}/dispose`, data).then(r => r.data),

  createJournalEntry: (data) => client.post("/journal-entries", data).then(r => r.data),
  postJournalEntry: (eid, erp_journal_number) =>
    client.post(`/journal-entries/${eid}/confirm-posted`, { erp_journal_number }).then(r => r.data),

  certifyPopulation: (periodId, source) =>
    client.post(`/periods/${periodId}/certify-population`, { source }).then(r => r.data),
  computeReconciliation: (periodId) =>
    client.post(`/periods/${periodId}/compute-reconciliation`).then(r => r.data),
  investigate: (periodId) => client.get(`/periods/${periodId}/investigate`).then(r => r.data),
  completionBlockers: (periodId) => client.get(`/periods/${periodId}/completion-blockers`).then(r => r.data),

  complete: (periodId, data) => client.post(`/periods/${periodId}/complete`, data).then(r => r.data),
  lock: (periodId) => client.post(`/periods/${periodId}/lock`).then(r => r.data),
  unlock: (periodId, reason) => client.post(`/periods/${periodId}/unlock`, { reason }).then(r => r.data),

  auditLogs: (periodId) => client.get("/audit-logs", { params: { period_id: periodId, limit: 200 } }).then(r => r.data),
  snapshots: (periodId) => client.get(`/periods/${periodId}/snapshots`).then(r => r.data),

  // Registers (cross-period)
  listJournalEntries: (params = {}) => client.get("/journal-entries", { params }).then(r => r.data),
  getJournalEntry: (eid) => client.get(`/journal-entries/${eid}`).then(r => r.data),
  voidJournalEntry: (eid, reason) => client.post(`/journal-entries/${eid}/void`, { reason }).then(r => r.data),
  // J-1 lifecycle (Report 36-Addendum)
  patchJournalEntry: (eid, data) => client.patch(`/journal-entries/${eid}`, data).then(r => r.data),
  discardJournalEntry: (eid, reason) =>
    client.delete(`/journal-entries/${eid}`, { params: { reason } }).then(r => r.data),
  cloneJournalEntry: (eid) => client.post(`/journal-entries/${eid}/clone`).then(r => r.data),
  reverseJournalEntry: (eid, journal_date, reason) =>
    client.post(`/journal-entries/${eid}/reverse`, { journal_date, reason }).then(r => r.data),
  listDuplicateClusters: (params = {}) => client.get("/duplicate-clusters", { params }).then(r => r.data),
  getDuplicateCluster: (cid) => client.get(`/duplicate-clusters/${cid}`).then(r => r.data),
  listBsjeTemplates: (params = {}) => client.get("/bsje-templates", { params }).then(r => r.data),
  createBsjeTemplate: (data) => client.post("/bsje-templates", data).then(r => r.data),
  patchBsjeTemplate: (tid, data) => client.patch(`/bsje-templates/${tid}`, data).then(r => r.data),

  // Accounts
  listAccounts: (includeArchived = false) =>
    client.get("/accounts", { params: { include_archived: includeArchived } }).then(r => r.data),
  getAccount: (aid) => client.get(`/accounts/${aid}`).then(r => r.data),
  createAccount: (data) => client.post("/accounts", data).then(r => r.data),
  patchAccount: (aid, data) => client.patch(`/accounts/${aid}`, data).then(r => r.data),
  deleteAccount: (aid) => client.delete(`/accounts/${aid}`).then(r => r.data),

  // GOV-025 Recognition Engine
  runRecognition: (periodId) => client.post(`/periods/${periodId}/recommendations/run`).then(r => r.data),
  listRecommendations: (periodId, params = {}) =>
    client.get(`/periods/${periodId}/recommendations`, { params }).then(r => r.data),
  acceptRecommendation: (rid, body = {}) => client.post(`/recommendations/${rid}/accept`, body).then(r => r.data),
  rejectRecommendation: (rid, reason) => client.post(`/recommendations/${rid}/reject`, { reason }).then(r => r.data),
  overrideRecommendation: (rid, rationale) => client.post(`/recommendations/${rid}/override`, { rationale }).then(r => r.data),
  bulkAcceptRecommendations: (periodId, bands) =>
    client.post(`/periods/${periodId}/recommendations/bulk-accept`, { bands }).then(r => r.data),

  // ─── Auth ───────────────────────────────────────────────────────────
  me: () => client.get("/auth/me").then(r => r.data),
  logout: () => client.post("/auth/logout").then(r => r.data),
  loginUrl: () => `${API}/auth/microsoft/login`,

  // ── Phase 5A · Administration ────────────────────────────────────────────
  getSettings: () => client.get("/settings").then(r => r.data),
  saveSettings: (payload) => client.put("/settings", payload).then(r => r.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/settings/logo", fd).then(r => r.data);
  },
  deleteLogo: () => client.delete("/settings/logo").then(r => r.data),
  governanceStatus: (periodId) =>
    client.get(`/periods/${periodId}/governance-status`).then(r => r.data),

  // ── Phase G-A · Certifications ───────────────────────────────────────────
  certifyPeriod: (periodId, basis = "controller_attestation") =>
    client.post(`/periods/${periodId}/certify`, { basis }).then(r => r.data),
  recertifyPeriod: (periodId, reason) =>
    client.post(`/periods/${periodId}/recertify`, { reason }).then(r => r.data),
  unlockPeriod: (periodId, reason) =>
    client.post(`/periods/${periodId}/unlock`, { reason }).then(r => r.data),
  lockPeriod: (periodId) =>
    client.post(`/periods/${periodId}/lock`).then(r => r.data),
  latestCertification: (periodId) =>
    client.get(`/periods/${periodId}/certification`)
      .then(r => r.data).catch(e => {
        if (e.response?.status === 404) return null;
        throw e;
      }),
  certificationHistory: (periodId) =>
    client.get(`/periods/${periodId}/certifications`).then(r => r.data),
  governanceDrift: (periodId) =>
    client.get(`/periods/${periodId}/governance-drift`).then(r => r.data),

  checkContrast: (text, background) =>
    client.post("/settings/check-contrast", { text, background }).then(r => r.data),
  assetUrl: (assetId) => `${API}/assets/${assetId}`,

  // ── Ad-blocker safe PDF generation (Feb 2026) ────────────────────────────
  // POST → returns { asset_id, filename }. Client then opens
  // `${API}/assets/{asset_id}` in a new tab — opaque URL identical in shape
  // to the logo asset URL that ad-blockers don't match. This eliminates
  // ERR_BLOCKED_BY_CLIENT permanently.
  generateCertificationPackage: (periodId) =>
    client.post(`/periods/${periodId}/certification_package/generate`)
          .then(r => r.data),
  generateBinder: (periodId) =>
    client.post(`/periods/${periodId}/binder/generate`)
          .then(r => r.data),

  // ── Phase 5D/5E · Reports ────────────────────────────────────────────────
  listReports: () => client.get("/reports").then(r => r.data),
  listReportRuns: (limit = 50) =>
    client.get("/reports/runs", { params: { limit } }).then(r => r.data),
  readinessState: () => client.get("/reports/readiness").then(r => r.data),
  runReport: (reportId, fmt, payload = {}) =>
    client.post(`/reports/${reportId}/run`, payload, {
      params: { fmt },
      responseType: "blob",
    }).then(r => ({ blob: r.data, sha256: r.headers["x-report-sha256"] || "" })),

  // ── Bank Statement Certification Pipeline (Aug 2026) ─────────────────────
  uploadStatementPdf: (batchId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/import-batches/${batchId}/statement`, fd).then(r => r.data);
  },
  runStatementPipeline: (docId) =>
    client.post(`/statement-documents/${docId}/run`, {}).then(r => r.data),
  overrideStatementValidation: (valId, reason) =>
    client.post(`/statement-validations/${valId}/override`, { reason }).then(r => r.data),
  statementReport: (docId) =>
    client.get(`/statement-documents/${docId}/report`).then(r => r.data),
  batchCertification: (batchId) =>
    client.get(`/import-batches/${batchId}/certification`).then(r => r.data),
  periodBankCertification: (periodId) =>
    client.get(`/periods/${periodId}/bank-certification`)
          .then(r => r.data)
          .catch(e => { if (e.response?.status === 404) return null; throw e; }),
  periodBankCertificationHistory: (periodId) =>
    client.get(`/periods/${periodId}/bank-certifications`).then(r => r.data),
  statementLibrary: () =>
    client.get(`/statement-library`).then(r => r.data),
  statementPdfUrl: (docId) => `${API}/statement-documents/${docId}/pdf`,
  importBankFromPdf: (periodId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/periods/${periodId}/import-bank-from-pdf`, fd, { timeout: 300000 })
                 .then(r => r.data);
  },
};

export const fmt = {
  currency: (n, opts = {}) => {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const v = Number(n);
    const abs = Math.abs(v).toLocaleString("en-CA", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    return v < 0 ? `(${abs})` : abs;
  },
  signed: (n) => {
    if (n === null || n === undefined || isNaN(n)) return "—";
    return Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: "auto" });
  },
  date: (s) => {
    if (!s) return "—";
    // Accounting dates are calendar dates, not timestamps. Parsing
    // "2026-06-30" as UTC midnight and rendering in the user's local
    // timezone (Eastern Canada = UTC-4/-5) rolls the date back one day.
    // For plain YYYY-MM-DD, build the Date in the local timezone.
    if (typeof s === "string") {
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
        }
      }
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
  },
  dateTime: (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  },
};
